<h2 mat-dialog-title class="dialog-title">
  {{ mode === 'add' ? 'Add New Truck' : 'Update Truck' }}
</h2>

<mat-dialog-content>
  <div class="template-row">
    <mat-form-field appearance="outline" [formGroup]="templateForm" class="template-field">
      <mat-label>Template</mat-label>

      <mat-select formControlName="id" (selectionChange)="onTemplateChange()">
        <mat-option [value]="null">None</mat-option>

        @for (template of templates; track template.id) {
          <mat-option [value]="template.id">
            {{ template.name }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>

    <div class="template-actions">
      <button mat-icon-button matTooltip="Add" type="button" (click)="addTemplate()">
        <mat-icon>add_circle_outline</mat-icon>
      </button>

      @if (templateForm.get('id')?.value) {
        <button mat-icon-button matTooltip="Copy" type="button" (click)="addTemplate('copy')">
          <mat-icon>content_copy</mat-icon>
        </button>

        <button mat-icon-button matTooltip="Update" type="button" (click)="updateTemplate()">
          <mat-icon>update</mat-icon>
        </button>

        <button mat-icon-button matTooltip="Delete" type="button" (click)="deleteTemplate()">
          <mat-icon>highlight_remove</mat-icon>
        </button>
      }
    </div>
  </div>

  <form [formGroup]="form" class="truck-form">
    <mat-form-field appearance="outline" class="medium-field">
      <mat-label>Available date range</mat-label>

      <mat-date-range-input [rangePicker]="availablePicker">
        <input
          matStartDate
          formControlName="available_start_date"
          placeholder="Start date"
          [min]="today"
        />

        <input
          matEndDate
          formControlName="available_end_date"
          placeholder="End date"
          [min]="today"
        />
      </mat-date-range-input>

      <mat-datepicker-toggle matIconSuffix [for]="availablePicker"></mat-datepicker-toggle>
      <mat-date-range-picker #availablePicker></mat-date-range-picker>

      @if (form.get('available_start_date')?.hasError('pastDate')) {
        <mat-error>Available start date cannot be in the past</mat-error>
      } @else if (form.get('available_start_date')?.invalid) {
        <mat-error>Invalid start date</mat-error>
      }

      @if (form.get('available_end_date')?.hasError('pastDate')) {
        <mat-error>Available end date cannot be in the past</mat-error>
      } @else if (form.get('available_end_date')?.invalid) {
        <mat-error>Invalid end date</mat-error>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="medium-field">
      <mat-label>Origin</mat-label>

      <input
        #originInput
        type="text"
        placeholder="Choose an origin"
        matInput
        formControlName="origin"
        [matAutocomplete]="pickAuto"
        (input)="filterOrigin()"
        (focus)="filterOrigin()"
      />

      <span matSuffix class="autocomplete-spinner">
        @if (loadingOrigin()) {
          <mat-spinner diameter="20" strokeWidth="3"></mat-spinner>
        }
      </span>

      <mat-autocomplete requireSelection #pickAuto="matAutocomplete">
        @for (option of filteredOriginOptions; track option.id) {
          <mat-option
            [value]="formatLocation(option)"
            (onSelectionChange)="onOriginSelected(option)"
          >
            {{ formatLocation(option) }}
          </mat-option>
        }
      </mat-autocomplete>

      @if (form.get('origin')?.hasError('required') && form.get('origin')?.touched) {
        <mat-error>Origin is required</mat-error>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="small-field">
      <mat-label>Axle count</mat-label>
      <input matInput formControlName="axle_count" type="number" />

      @if (form.get('axle_count')?.invalid) {
        <mat-error>Axle count is invalid</mat-error>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="small-field">
      <mat-label>Max weight</mat-label>
      <input matInput formControlName="max_weight" type="number" />

      @if (form.get('max_weight')?.invalid) {
        <mat-error>Max weight is required</mat-error>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="small-field">
      <mat-label>Well length (ft)</mat-label>

      <input
        matInput
        formControlName="well_length"
        type="number"
        step="1"
      />

      @if (form.get('well_length')?.invalid) {
        <mat-error>Well length is required</mat-error>
      }

      @if (form.get('well_length')?.value) {
        <mat-hint>
          {{ toFeetAndInchesFromFeet(form.get('well_length')?.value) }}
        </mat-hint>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="small-field">
      <mat-label>Min rate</mat-label>
      <span matTextPrefix>$&nbsp;&nbsp;</span>
      <input matInput formControlName="min_rate" type="number" step="100" />

      @if (form.get('min_rate')?.invalid) {
        <mat-error>Min rate is invalid</mat-error>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="small-field">
      <mat-label>Min RPM</mat-label>
      <span matTextPrefix>$&nbsp;&nbsp;</span>
      <input matInput formControlName="min_rpm" type="number" step="0.5" />

      @if (form.get('min_rpm')?.invalid) {
        <mat-error>Min RPM is invalid</mat-error>
      }
    </mat-form-field>

    <div class="carrier-row">
      <mat-form-field appearance="outline" class="medium-field">
        <mat-label>Reference</mat-label>
        <input matInput formControlName="reference" />
      </mat-form-field>

      @if (carriers.length > 1) {
        <mat-form-field appearance="outline" class="medium-field">
          <mat-label>Carrier</mat-label>

          <mat-select formControlName="carrier_id" (selectionChange)="onCarrierChange($event)">
            @for (carrier of carriers; track carrier.id) {
              <mat-option [value]="carrier.id">
                {{ carrier.name }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
      }
    </div>

    <mat-form-field appearance="outline" class="notes-field">
      <mat-label>Note</mat-label>
      <textarea matInput formControlName="note"></textarea>
    </mat-form-field>

    <div class="checkbox-row">
      <mat-checkbox formControlName="military_base_access">Military</mat-checkbox>
      <mat-checkbox formControlName="twic">TWIC</mat-checkbox>
      <mat-checkbox formControlName="emodal">E-Modal</mat-checkbox>
      <mat-checkbox formControlName="outriggers">Outriggers</mat-checkbox>
      <mat-checkbox formControlName="tarp">Tarp</mat-checkbox>
      <mat-checkbox formControlName="hazmat">Hazmat</mat-checkbox>
    </div>
  </form>
</mat-dialog-content>

<mat-dialog-actions align="end">
  @if (mode === 'edit' && truck) {
    <button
      mat-button
      type="button"
      class="danger-button"
      (click)="deleteTruck()"
    >
      Delete
    </button>
  }

  <button mat-button type="button" (click)="onCancel()">
    Close
  </button>

  <button 
    mat-button 
    type="button" 
    class="lightgreen-button" 
    (click)="onSubmit()"
    [disabled]="form.invalid"
  >
    {{ mode === 'add' ? 'Add' : 'Update' }}
  </button>
</mat-dialog-actions>